import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCookies } from 'react-cookie'
import { useHistory, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { motion } from 'framer-motion'
import isEmpty from 'lodash/fp/isEmpty'
import Box from '@aura/components/box'
import Button from '@aura/components/button'
import Chip from '@aura/components/chip'
import Checkbox from '@aura/components/checkbox'
import Divider from '@aura/components/divider'
import Grid from '@aura/components/grid'
import IconButton from '@aura/components/icon-button'
import Link from '@aura/components/link'
import Typography from '@aura/components/typography'
import TextField from '@aura/components/text-field'
import HorizontalGroup from '@aura/components/horizontal-group'
import Form from '@aura/forms/form'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import { FormLabel, InputAdornment } from '@mui/material'
import authStatusType from '../../../../../constants/auth-status'
import language from '../../../../../constants/languages'
import loginLayoutType from '../../../../../constants/login-layout'
import loginTitleLayouts from '../../../../../constants/login-title-layout'
import { ANALYTICS_REGISTER, LOGIN_APP_ROUTE, LOGIN_ROUTE, DEFAULT_REGISTER } from '../../../../../constants/routes'
import { cookieNameRememberMe, cookieNameLoginAppCookie, cookieNameRedirectUrl, isFrench } from '../../../../../constants/app'
import Footer from '../../../../../components/footer-login'
import FooterCustom from '../../../../../components/footer-login-custom'
import FooterCentral from '../../../../../components/footer-login-central'
import LanguageButton from '../../../../../components/language-button'
import formFields, { initialValues } from '../constants/form-fields'
import mataLogo from '../../../../../../public/img/analytics/mata_logo.png'
import rbcdxLogoEn from '../../../../../../public/img/rbcdx/rbcdx_logo_en.svg?url'
import rbcdxLogoFr from '../../../../../../public/img/rbcdx/rbcdx_logo_fr.svg?url'
import rbcdxLogoEnMob from '../../../../../../public/img/rbcdx/rbcdx_logo_en_mob.svg?url'
import rbcdxLogoFrMob from '../../../../../../public/img/rbcdx/rbcdx_logo_fr_mob.svg?url'
import { appEnum } from '../../../../../appConfig'
import { useConfig } from '../../../../../utils/config'
import { appExists } from '../../../../../utils/app-context'
import { formatErrorKey } from '../../../../../utils/formatErrorKey'
import styles from './styles'


const DefaultLogin = ({
    applicationContext,
    authStatus,
    errorForgotPwd,
    errorAuth,
    forgotPwdProgress,
    forgotPwdSuccessMsg,
    magicLinkLoginMsg,
    errorMagicLinkLogin,
    onAuthenticate,
    onForgotPassword,
    onRequestLoginMagicLink
}) => {
    const config = useConfig()
    const { appValue,
        banner,
        footer,
        footerHidden,
        frenchInfo,
        informationLink,
        informationLinkName,
        loginLayout,
        loginTitle,
        loginTitleLayoutType,
        logoArialLabel,
        logo,
        loginUrl,
        logoFr,
        registerUrl,
        requestAccess,
        textInputFilled,
        welcomeHeaderDesc,
        welcomeHeaderDescTop,
        welcomeHeaderLogo,
        welcomeHeaderLogoFr,
        welcomeHeaderTitle,
        welcomeHeaderSubTitle,
        welcomeHeaderTitleItalic,
        qrCode } = applicationContext

    const welcomeHeaderLogoAlt = appValue + ' logo'
    const bannerBackgroundMemo = useMemo(() => `url(${banner})`, [ banner ])
    const bannerBackground = {
        backgroundImage: bannerBackgroundMemo,
    }
    const forgotMailSent = 'We\'ve recently sent a password reset request. Please check your mailbox or retry later.'
    const magicLinkSent = 'We\'ve recently sent a magic link. Please check your mailbox or retry later.'
    const ref = useRef()
    const history = useHistory()

    const [ cookies, setCookie, removeCookie ] = useCookies([ cookieNameRememberMe, cookieNameLoginAppCookie, cookieNameRedirectUrl ])

    const { t: translate, i18n } = useTranslation()

    const [ selectedLanguage, setSelectedLanguage ] = useState(i18n.language)
    const [ showPassword, setShowPassword ] = useState(false)
    const [ forgotPwdClicked, setForgotPwdClicked ] = useState(false)
    const [ isMobile ] = useState(window.innerWidth < 450)
    const [ lastAction, setLastAction ] = useState(null) // 'forgotPwd' or 'magicLink'
    const { locale, appName } = useParams()
    const isAnalyticsApp = appValue === appEnum.analytics
    const isRequestAccessEnabled = useMemo(() => {
        return isAnalyticsApp && (requestAccess === true || requestAccess === 'true')
    }, [ isAnalyticsApp, requestAccess ])

    document.title = translate(loginTitle)

    useEffect(() => {
        if (appName && !appExists(appName)) {
            history.push(LOGIN_APP_ROUTE)
        } else {
            history.push(window.location.pathname.toLowerCase())
        }
    }, [ appName ])

    useEffect(() => {
        const defaultLang = language.ENGLISH
        const loginHome = window.location.pathname.split('/login')[0] + '/login'
        if (locale === language.ENGLISH || locale === language.FRENCH) {
            setSelectedLanguage(locale)
            i18n.changeLanguage(locale)
        } else if (isFrench(selectedLanguage)) {
            setSelectedLanguage(language.FRENCH)
            i18n.changeLanguage(language.FRENCH)
            history.push(loginHome + `/${language.FRENCH}`)
        } else {
            setSelectedLanguage(defaultLang)
            i18n.changeLanguage(defaultLang)
            history.push(loginHome + `/${defaultLang}`)
        }
    }, [ locale ])

    useEffect(() => {
        setSelectedLanguage(i18n.language)
    }, [ i18n.language ])

    const { REMEMBER_ME, LOGIN, PASSWORD } = formFields

    const handleClickShowPassword = useCallback(() => {
        setShowPassword(!showPassword)
    })

    const handleForgotPassword = useCallback(async () => {
        const { values, validateForm } = ref.current.getContext()
        setForgotPwdClicked(true)
        setLastAction('forgotPwd')

        setTimeout(async () => {
            const errors = await validateForm(values)
            if (isEmpty(errors)) {
                onForgotPassword(values[LOGIN])
            }
            setForgotPwdClicked(false)
        }, 100)
    })
    const handlePasswordlessLogin = useCallback(async () => {
        const { values, setFieldError } = ref.current.getContext()
        let errors = {}
        if (!values[LOGIN]) {
            errors[LOGIN] = translate('required')
        } else if (!/^[A-Z0-9._%`~|}{^?=/*&$#!+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values[LOGIN])) {
            errors[LOGIN] = translate('incorrectEmail')
        }
        setFieldError(LOGIN, errors[LOGIN] || '')
        if (!errors[LOGIN]) {
            setLastAction('magicLink')
            onRequestLoginMagicLink({ login: values[LOGIN ] })
        }
    }, [ translate, LOGIN ])
    

    useEffect(() => {
        if (errorAuth && ref.current) {
            const { setFieldError, setFieldTouched } = ref.current.getContext()
            setFieldTouched(PASSWORD, true, false)
            setFieldError(PASSWORD, translate('oktaApiResponse.' + formatErrorKey(errorAuth)))
        }
    }, [ errorAuth, translate ])

    const validateFormDetails = useCallback(async event => {
        const { setFieldError, validateForm } = ref.current.getContext()
        await validateForm()
        errorAuth && setFieldError(PASSWORD, translate('oktaApiResponse.' + formatErrorKey(errorAuth)))
    })

    const mainDomain = useMemo(() => window.location.hostname.substring(window.location.hostname.indexOf('.')), [])

    const initValues = useMemo(() => ({
        ...initialValues,
        login: cookies[cookieNameRememberMe] || '',
        rememberMe: cookies[cookieNameRememberMe] || false
    }), [ cookies[cookieNameRememberMe] ])

    if (window.location.search.length > 0) {
        document.cookie = `${cookieNameRedirectUrl}=${window.location.search};domain=${mainDomain};path=/;samesite=strict;secure`
    }

    const handleLoginClick = values => {
        if (values.rememberMe) {
            removeCookie(cookieNameRememberMe, { domain: mainDomain, path: '/', sameSite: 'strict', secure: true })
            setCookie(cookieNameRememberMe, values.login, { domain: mainDomain, path: '/', sameSite: 'strict', secure: true })
        } else {
            removeCookie(cookieNameRememberMe, { domain: mainDomain, path: '/', sameSite: 'strict', secure: true })
        }

        setCookie(cookieNameLoginAppCookie, loginUrl || '', { domain: mainDomain, path: '/', sameSite: 'strict', secure: true })
        onAuthenticate(values)
    }

    const validate = values => {
        let errors = {}
        let loginCheck = false
        let passwordCheck = false

        if (forgotPwdClicked) {
            loginCheck = true
        } else {
            passwordCheck = true
            loginCheck = true
        }

        if (loginCheck) {
            if (!values[LOGIN]) {
                errors[LOGIN] = translate('required')
            }

            if (values[LOGIN] && !/^[A-Z0-9._%`~|}{^?=/*&$#!+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values[LOGIN])) {
                errors = { ...errors, [LOGIN]: translate('incorrectEmail') }
            }
        }

        if (passwordCheck) {
            if (!values[PASSWORD]) {
                errors[PASSWORD] = translate('required')
            }
        }

        return errors
    }
    // we are showing success message "An email has been sent to help you reset your password. Please check your email." message when we are receiving too many request code status. 
    const forgotPwdResponseHTML = useMemo(() => {
        if (forgotPwdSuccessMsg || errorForgotPwd === forgotMailSent) {
            return (
                <Box component='span' sx={styles.forgotPasswordSuccessMsg}>
                    {translate(`oktaApiResponse.${forgotPwdSuccessMsg || 'forgotPasswordRequest'}`)}
                </Box>
            )
        }

        if (errorForgotPwd) {
            return (
                <Box component='span' sx={styles.forgotPasswordMsg}>
                    {translate(`oktaApiResponse.${errorForgotPwd}`)}
                </Box>
            )
        }

        return <></>
    }, [ forgotPwdSuccessMsg, errorForgotPwd, forgotMailSent, translate ])

    const isProcessing = authStatus === authStatusType.PENDING || forgotPwdProgress

    const magicLinkResponseHTML = useMemo(() => {
        if (magicLinkLoginMsg) {
            return (
                <Box component='span' sx={ { ...styles.passwordLessSuccessMsg }}>
                    <InfoOutlineIcon sx={{ color: 'rgba(0, 131, 143, 1)' }} />
                    <Box component='span' sx={styles.passwordLessMsgText}> {translate(magicLinkLoginMsg)} </Box>                    
                </Box>
            )
        } else if (errorMagicLinkLogin) {
            const match = errorMagicLinkLogin.match(/Too many magic link requests\. Please try again in (\d+) minute/)
            if (match) {
                return (
                    <Box component='span' sx={styles.passwordLessTooManyErr}>
                        <InfoOutlineIcon sx={{ color: 'rgba(198, 40, 40, 1)' }} />
                        <Box component='span' sx={styles.passwordLessMsgText}>
                            {translate('magicLink.tooManyRequests')}
                        </Box>
                    </Box>
                )
            }
            return (
                <Box component='span' sx={styles.forgotPasswordMsg}>
                    {translate(errorMagicLinkLogin)}    
                </Box>
            )
        }

        return <></>
    }, [ magicLinkLoginMsg, errorMagicLinkLogin, magicLinkSent, translate ])

    // Show only the latest message (forgotPwd or magicLink)
    const latestResponseHTML = useMemo(() => {
        if (lastAction === 'magicLink') {
            return magicLinkResponseHTML
        } else if (lastAction === 'forgotPwd') {
            return forgotPwdResponseHTML
        }
        return <></>
    }, [ lastAction, magicLinkResponseHTML, forgotPwdResponseHTML ])

    const hasRBCLogo = useMemo(() => logo || logoFr, [ applicationContext ])

    const rbccmLogo = useMemo(() => {
        switch (i18n.language) {
            case language.ENGLISH:
                return <Box
                    alt='RBC Logo'
                    component='img'
                    src={logo}
                    sx={styles.logoEn} />
            case language.FRENCH:
                return <Box
                    alt='RBC Logo'
                    component='img'
                    src={logoFr}
                    sx={styles.logoFr} />
            default:
                return <Box
                    alt='RBC Logo'
                    component='img'
                    src={logo}
                    sx={styles.logoEn} />
        }
    }, [ i18n.language ])

    const languageIconHTML = (
        <LanguageButton
            clsName={
                loginLayout === loginLayoutType.CENTER
                    ? 'centralLanguageButton'
                    : appValue && appValue !== appEnum.analytics && appValue !== appEnum.rbcdx
                        ? 'languageButtonLogin'
                        : 'languageButtonLoginMain'
            }
            variant={loginLayout === loginLayoutType.CENTER ? 'text' : 'contained'}
            component={LOGIN_ROUTE}
            loginTitle={loginTitle}
            validateForm={validateFormDetails}
        />
    )

    const languageIconHTMLForMobileView = (
        <LanguageButton
            clsName={
                (appValue === appEnum.analytics || appValue === appEnum.rbcdx)
                    ? selectedLanguage === language.FRENCH ? 'mobileLanguageButtonCustomFr' : 'mobileLanguageButtonCustom'
                    : appValue
                        ? 'mobileLanguageButton'
                        : 'mobileLanguageButtonDefault'
            }
            variant='text'
            component={LOGIN_ROUTE}
            loginTitle={loginTitle}
            validateForm={validateFormDetails}
        />
    )
    const inputFieldsHTML = <>
        <Grid container sx={loginLayout === loginLayoutType.CENTER ? styles.emailContainerCentral : styles.emailContainer} direction='column' spacing={2}>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField autoFocus {...{
                    autoComplete: 'email',
                    field: LOGIN,
                    fullWidth: true,
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    label: translate('email'),
                    placeholder: translate('email'),
                    sx: styles.input,
                    variant: textInputFilled ? 'filled' : 'standard',
                }} />
            </Grid>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField {...{
                    autoComplete: 'current-password',
                    field: PASSWORD,
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    InputProps: {
                        endAdornment:
                        <InputAdornment position='end'>
                            <IconButton
                                aria-label='toggle password visibility'
                                onClick={handleClickShowPassword}>
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                        </InputAdornment>
                    },
                    label: translate('pw'),
                    placeholder: translate('pw'),
                    sx: styles.input,
                    type: showPassword ? 'text' : 'password',
                    variant: textInputFilled ? 'filled' : 'standard'
                }} />
            </Grid>
        </Grid>
        <Link
            href='#'
            onClick={handleForgotPassword}
            underline='none'
            sx={styles.forgotPassword}>
            <Box component='div'>{translate('forgotPw')}</Box>
        </Link>
        <Box component='div' sx={styles.forgotPwdResponse}>
            {latestResponseHTML}
        </Box>
    </>

    const inputFieldsHTMLCustom = <>
        <Grid container sx={styles.emailContainer} direction='column' spacing={2}>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField autoFocus {...{
                    autoComplete: 'email',
                    field: LOGIN,
                    fullWidth: true,
                    InputLabelProps: {
                        shrink: true,
                        sx: styles.inputCustomLabelProps
                    },
                    label: translate('email'),
                    placeholder: translate('email'),
                    sx: styles.inputCustom,
                    variant: 'standard'
                }} />
            </Grid>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField {...{
                    autoComplete: 'current-password',
                    field: PASSWORD,
                    fullWidth: true,
                    InputLabelProps: {
                        shrink: true,
                        sx: styles.inputCustomLabelProps
                    },
                    InputProps: {
                        endAdornment:
                            <InputAdornment position='end'>
                                <IconButton
                                    aria-label='toggle password visibility'
                                    onClick={handleClickShowPassword}
                                    sx={styles.inputCustomShowPassword}
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                    },
                    label: translate('pw'),
                    placeholder: translate('pw'),
                    sx: styles.inputCustom,
                    type: showPassword ? 'text' : 'password',
                    variant: 'standard'
                }} />
            </Grid>
        </Grid>
        <Grid item md={12} sx={styles.rememberMeContainerCustom}>
            <HorizontalGroup gutter={0}>
                <Checkbox id='rememberMe' sx={styles.checkboxMain} size='large' field={REMEMBER_ME} />
                <FormLabel for='rememberMe' sx={styles.rememberMeLabelMain}>{translate('rememberMe')}</FormLabel>
            </HorizontalGroup>
            <Box sx={styles.forgotPasswordBoxCustom} >
                <Link
                    href='#'
                    onClick={handleForgotPassword}
                    underline='none'
                    sx={styles.forgotPasswordCustom}>
                    <Box component='div'>{translate('forgotPw')}</Box>
                </Link>
            </Box>
        </Grid>
        <Box component='div' sx={styles.forgotPwdResponse}>
            {latestResponseHTML}
        </Box>
    </>

    const inputFieldsMainHTML = <>
        <Grid container sx={loginLayout === loginLayoutType.CENTER ? styles.emailContainerCentral : styles.emailMainContainer} direction='column' spacing={2}>
            <Box sx={styles.loginTextContainer}>
                <Typography sx={styles.loginText}>{translate('loginText')}</Typography>
            </Box>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField autoFocus {...{
                    autoComplete: 'email',
                    field: LOGIN,
                    fullWidth: true,
                    InputLabelProps: {
                        shrink: true,
                        sx: styles.inputCustomLabelProps
                    },
                    label: translate('email'),
                    placeholder: translate('email'),
                    sx: styles.inputCustom,
                    variant: 'standard'
                }} />
            </Grid>
            <Grid item md={12} xs={12} sx={styles.emailContainerField}>
                <TextField {...{
                    autoComplete: 'current-password',
                    field: PASSWORD,
                    fullWidth: true,
                    InputLabelProps: {
                        shrink: true,
                        sx: styles.inputCustomLabelProps
                    },
                    InputProps: {
                        endAdornment:
                            <InputAdornment position='end'>
                                <IconButton
                                    aria-label='toggle password visibility'
                                    onClick={handleClickShowPassword}
                                    sx={styles.inputCustomShowPassword}
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                    },
                    label: translate('pw'),
                    placeholder: translate('pw'),
                    sx: styles.inputCustom,
                    type: showPassword ? 'text' : 'password',
                    variant: 'standard'
                }} />
            </Grid>
        </Grid>
        <Grid item md={12} sx={styles.rememberMeContainerCustom}>
            <HorizontalGroup gutter={0}>
                <Checkbox id='rememberMe' sx={styles.checkboxMain} size='large' field={REMEMBER_ME} />
                <FormLabel for='rememberMe' sx={styles.rememberMeLabelMain}>{translate('rememberMe')}</FormLabel>
            </HorizontalGroup>
            <Box sx={styles.forgotPasswordBoxCustom} >
                <Link
                    href='#'
                    onClick={handleForgotPassword}
                    underline='none'
                    sx={styles.forgotPasswordCustom}>
                    <Box component='div'>{translate('forgotPw')}</Box>
                </Link>
            </Box>
        </Grid>
        <Box component='div' sx={styles.forgotPwdResponse}>
            {latestResponseHTML}
        </Box>
    </>

    const leftRightContainerHTMLDefault = <>
        <Grid item sx={styles.leftContainer}>
            <Grid direction='column' sx={styles.loginContainer}>
                {hasRBCLogo && <Box component='div' sx={styles.logoContainerDefault}>
                    <Link sx={appValue ? styles.logoContainer : styles.logoContainerMain} target='_blank' rel='noopener noreferrer' aria-label={translate('ariaLabel.rbcLogo')} href={translate('rbccmLink')}>
                        {rbccmLogo}
                    </Link>
                    {languageIconHTMLForMobileView}
                </Box>}
                { (frenchInfo && selectedLanguage === language.FRENCH) && <Box sx={styles.frenchInfoContainer}>
                    <Chip
                        label={translate('frenchInfo')}
                        size='medium'
                        variant='filled'
                        sx={selectedLanguage === language.ENGLISH ? styles.frenchInfoContainerMain : styles.frenchInfoContainerMain}
                    />
                </Box>}
                <Grid container direction='column' spacing={0}>
                    <Grid container sx={styles.loginForm} direction='column' spacing={0}>
                        {appValue ? inputFieldsHTML : inputFieldsMainHTML}
                        <Button
                            color='primary'
                            disabled={isProcessing}
                            sx={styles.buttonCustom}
                            type='submit'
                            variant='contained'>{translate('login')}
                        </Button>
                        {
                            config.passwordLessLogin === 'true'
                            && <Button
                                color='primary'
                                disabled={isProcessing}
                                sx={{ ...styles.buttonMagicLink }}
                                variant='outlined'
                                onClick={handlePasswordlessLogin}
                            >
                                {translate('passwordLessLogin')}
                            </Button>
                        }
                        {(requestAccess === true || requestAccess === 'true') && <>
                            <Box component='div' sx={styles.orContainer}>
                                <Box component='div' sx={ { ...styles.divider, ...styles.firstDivider }} />
                                <span style={styles.or}>{translate('or')}</span>
                                <Box component='div' sx={ { ...styles.divider,...styles.lastDivider }} />
                            </Box>
                            <Button
                                color='primary'
                                disabled={isProcessing}
                                sx={{ ...styles.buttonRequestAccess }}
                                href={registerUrl || DEFAULT_REGISTER}
                                variant='outlined'>{translate('requestAcess')}
                            </Button>
                        </>
                        }
                    </Grid>
                </Grid>
                <Footer isReadOnly={isProcessing} appValue={appValue} />
            </Grid>
        </Grid>
    </>

    const leftRightContainerHTMLMATA = <>
        <Grid item sx={styles.leftContainerCustom}>
            <Grid direction='column' sx={styles.loginContainerCustom}>
                <Box component='div' sx={styles.logoContainerDefault}>
                    <Link
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={translate('ariaLabel.rbcLogo')}
                        href={translate('rbccmLink')}
                    >
                        {rbccmLogo}
                    </Link>
                    {languageIconHTMLForMobileView}
                </Box>
                <Box sx={styles.mataLogo} component='img' alt={translate('analytics.mataLogo')} src={mataLogo} />
                <Box sx={styles.loginTextContainerCustom}>
                    <Typography sx={styles.loginTextCustom}>
                        {translate('loginText')}
                    </Typography>
                </Box>
                <Grid container direction='column' spacing={0}>
                    <Grid container sx={styles.loginForm} direction='column' spacing={0}>
                        {inputFieldsHTMLCustom}
                        <Button
                            color='primary'
                            disabled={isProcessing}
                            sx={styles.buttonCustom}
                            type='submit'
                            variant='contained'
                        >
                            {translate('loginText')}
                        </Button>
                        {isRequestAccessEnabled && (
                            <Button
                                color='primary'
                                disabled={isProcessing}
                                sx={{ ...styles.buttonRequestAccess }}
                                href={registerUrl || ANALYTICS_REGISTER}
                                variant='outlined'
                            >
                                {translate('requestAcess')}
                            </Button>
                        )}
                    </Grid>
                </Grid>
                <Box sx={styles.needHelpBoxCustom}>
                    <Link
                        href={config.mataMailTo ? config.mataMailTo : 'mailto:mata@rbccm.com'}
                        underline='none'
                        sx={{ display: 'block', textDecoration: 'none' }}
                    >
                        <Typography sx={styles.needHelpTextCustom}>
                            {translate('needHelp')}
                        </Typography>
                    </Link>
                </Box>
                <FooterCustom isReadOnly={isProcessing} />
            </Grid>
        </Grid>
    </>

    const leftRightContainerHTMLRBCDX = <Grid item sx={styles.leftContainerCustom}>
        <Grid direction='column' sx={styles.loginContainerCustom}>
            <Box component='div' sx={styles.logoContainerDefault}>
                <Link
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={translate('ariaLabel.rbcLogo')}
                    href={translate('rbccmLink')}
                >
                    {rbccmLogo}
                </Link>
                {languageIconHTMLForMobileView}
            </Box>
            <Box sx={styles.rbcdxLogoContainer}>
                <Box
                    component='img'
                    alt={translate('rbcdx.logo')}
                    src={
                        isMobile
                            ? (selectedLanguage === language.ENGLISH ? rbcdxLogoEnMob : rbcdxLogoFrMob)
                            : (selectedLanguage === language.ENGLISH ? rbcdxLogoEn : rbcdxLogoFr)
                    }
                />
            </Box>
            <Box sx={selectedLanguage === language.ENGLISH ? styles.loginTextContainerCustom : styles.loginTextContainerFrDX}>
                <Typography sx={styles.loginTextCustom}>
                    {translate('loginText')}
                </Typography>
            </Box>
            <Grid container direction='column' spacing={0}>
                <Grid container sx={styles.loginForm} direction='column' spacing={0}>
                    {inputFieldsHTMLCustom}
                    <Button
                        color='primary'
                        disabled={isProcessing}
                        sx={styles.buttonCustom}
                        type='submit'
                        variant='contained'
                    >
                        {translate('loginText')}
                    </Button>
                </Grid>
            </Grid>
            <Box sx={styles.needHelpBoxDX} />
            <FooterCustom isReadOnly={isProcessing} />
        </Grid>
    </Grid>

    const leftRightContainerHTML = <Grid item lg={3} md={4} sm={5} xs={12} sx={styles.leftContainer}>
        <Grid direction='column' sx={styles.loginContainer}>
            {languageIconHTMLForMobileView}
            {hasRBCLogo && <Link sx={styles.logoContainer} target='_blank' rel='noopener noreferrer' aria-label={translate('ariaLabel.rbcLogo')} href={translate('rbccmLink')}>
                {rbccmLogo}
            </Link>}
            <Typography sx={styles.header} >{translate(loginTitle)}</Typography>
            <Divider sx={styles.headerDivider} />
            { (frenchInfo && selectedLanguage === language.FRENCH) && <Box sx={styles.frenchInfoContainer}>
                <Chip
                    label={translate('frenchInfo')}
                    size='medium'
                    variant='filled'
                    sx={selectedLanguage === language.ENGLISH ? styles.frenchInfoContainerMain : styles.frenchInfoContainerMain}
                />
            </Box>}
            <Grid container direction='column' spacing={0}>
                <Grid container sx={styles.loginForm} direction='column' spacing={0}>
                    {inputFieldsHTML}
                    <Button
                        color='primary'
                        disabled={isProcessing}
                        sx={styles.button}
                        type='submit'
                        variant='contained'>{translate('login')}
                    </Button>
                    <Grid item md={12} sx={styles.rememberMeContainer}>
                        <HorizontalGroup gutter={0}>
                            <Checkbox id='rememberMe' sx={styles.checkbox} field={REMEMBER_ME} />
                            <FormLabel for='rememberMe' sx={styles.rememberMeLabel}>{translate('rememberMe')}</FormLabel>
                        </HorizontalGroup>
                    </Grid>
                    {isRequestAccessEnabled && <>
                        <Box component='div' sx={styles.orDivider}>
                            <Box component='div' sx={styles.innerOr}>{translate('or')}</Box>
                        </Box>
                        <Button
                            color='primary'
                            disabled={isProcessing}
                            sx={{ ...styles.button }}
                            href={registerUrl || ANALYTICS_REGISTER}
                            variant='outlined'>{translate('requestAcess')}
                        </Button>
                    </>}
                    <Box component='p' sx={styles.moreInfoText}>{translate('moreInfo')}</Box>
                </Grid>
            </Grid>
            <Footer isReadOnly={isProcessing} />
        </Grid>
    </Grid>

    const centralLayoutHTML = <>
        <Grid container spacing={0} sx={styles.centralLoginPage}>
            <Grid item lg={3} md={4} sm={5} xs={12} sx={styles.centralContainer}>
                {languageIconHTML}
                <Grid container direction='column' sx={styles.centralLoginContainer}>
                    <Link sx={styles.centralLogoContainer} target='_blank' rel='noopener noreferrer' aria-label={logoArialLabel && translate(logoArialLabel)} href={translate('rbccmLink')}>
                        <Box
                            alt='RBC Logo'
                            component='img'
                            src={translate(logo)}
                            sx={styles.centralLogo}
                        />
                    </Link>
                    <Typography component='h1' sx={styles.centralHeader} >{translate(loginTitle)}</Typography>
                    { (frenchInfo && selectedLanguage === language.FRENCH) && <Box sx={styles.frenchInfoContainer}>
                        <Chip
                            label={translate('frenchInfo')}
                            size='medium'
                            variant='filled'
                            sx={selectedLanguage === language.ENGLISH ? styles.frenchInfoContainerMain : styles.frenchInfoContainerMain}
                        />
                    </Box>}
                    <Grid container sx={styles.loginForm} direction='column' spacing={0}>
                        {inputFieldsHTML}
                        <Grid item md={12} sx={styles.centralRememberMeContainer}>
                            <HorizontalGroup gutter={0}>
                                <Checkbox id='rememberMe' size='large' sx={styles.centralCheckbox} field={REMEMBER_ME} />
                                <FormLabel for='rememberMe' sx={styles.centralRememberMeLabel}>{translate('rememberMyUserId')}</FormLabel>
                            </HorizontalGroup>
                        </Grid>
                        <Button
                            color='primary'
                            disabled={isProcessing}
                            sx={styles.centralButton}
                            type='submit'
                            variant='contained'>{translate('login')}
                        </Button>
                    </Grid>
                    <FooterCentral
                        applicationContext={footer}
                        isReadOnly={isProcessing}
                    />
                </Grid>
            </Grid>
        </Grid>
    </>

    const headerLogoSrc = useMemo(() => {
        let logoSrc = null
        if (welcomeHeaderLogo && selectedLanguage === language.ENGLISH) {
            logoSrc = welcomeHeaderLogo
        } else if (welcomeHeaderLogoFr && selectedLanguage === language.FRENCH) {
            logoSrc = welcomeHeaderLogoFr
        }
        return logoSrc
    }, [ selectedLanguage ])

    const footerHTML = footerHidden
        ? <></>
        : <>
            <Typography sx={styles.title}>{translate(welcomeHeaderTitle)}</Typography>
            <Typography sx={styles.description}>{translate(welcomeHeaderDesc)}</Typography>
        </>

    const footerHTMLDefault = footerHidden
        ? <></>
        : <>
            <Typography sx={styles.titleDefault}>{translate(welcomeHeaderTitle)}</Typography>
            <Typography sx={styles.welcomeSubHeader}>{translate(welcomeHeaderDescTop)}</Typography>
            <Typography sx={styles.descriptionDefault}>{translate(welcomeHeaderDesc)}</Typography>
        </>

    const leftRightLayoutHTML = <>
        <Grid container spacing={0} sx={styles.loginPage}>
            {leftRightContainerHTML}
            <Grid sx={styles.grid} style={bannerBackground} item lg={9} md={8} sm={7} xs={0}>
                {languageIconHTML}
                <Grid container alignItems='start' sx={loginTitleLayoutType === loginTitleLayouts.ALTERNATIVE ? styles.grid : styles.container}>
                    <div sx={styles.header}>
                        {headerLogoSrc && (
                            <Box
                                alt={welcomeHeaderLogoAlt}
                                component='img'
                                src={headerLogoSrc}
                            />
                        )}
                    </div>
                    {loginTitleLayoutType === loginTitleLayouts.ALTERNATIVE ? (
                        <Grid container alignItems='start' sx={styles.containerAlternative} direction='column'>
                            <Typography sx={styles.descriptionTop}>
                                {translate(welcomeHeaderDescTop)}
                            </Typography>
                            <Typography sx={styles.titleAlernative}>
                                {translate(welcomeHeaderTitle)}
                                <Box component='span' sx={styles.titleItalic}>{translate(welcomeHeaderTitleItalic)}</Box>
                            </Typography>
                            <Typography sx={styles.descriptionAlternative}>
                                {translate(welcomeHeaderDesc)}
                                <Link href={informationLink} target='_blank' sx={styles.link} underline='always' rel='noopener noreferrer'>
                                    {translate(informationLinkName)}
                                </Link>
                            </Typography>
                        </Grid>
                    ) : footerHTML}
                </Grid>
            </Grid>
        </Grid>
    </>

    const mataLayoutHTML = <>
        <Grid container spacing={0} sx={styles.loginPage}>
            {leftRightContainerHTMLMATA}
            <Grid sx={styles.gridCustom} style={bannerBackground} item>
                <Box sx={styles.languageIconContainerMain}>
                    {languageIconHTML}
                </Box>
                <Grid container alignItems='start' sx={selectedLanguage === language.ENGLISH ? styles.containerMataEn : styles.containerMataFr}>
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <Box sx={styles.bannerContentMata}>
                            <Typography variant='h1' sx={styles.welcomeHeaderMata}>
                                {translate(welcomeHeaderTitle)}
                            </Typography>
                            <Typography variant='h1' sx={styles.welcomeSubHeaderMata}>
                                {translate(welcomeHeaderDescTop)}
                            </Typography>
                            <Typography variant='body1' sx={styles.welcomeHeaderDescMata}>
                                {translate(welcomeHeaderDesc)}
                            </Typography>
                        </Box>
                    </motion.div>
                    <Box sx={ selectedLanguage === language.ENGLISH ? styles.customBannerBoxesEn : styles.customBannerBoxesFr }>
                        {[
                            translate('analytics.equities'),
                            translate('analytics.futures'),
                            translate('analytics.foreignExchange'),
                            translate('analytics.index'),
                            translate('analytics.api'),
                        ].map((text, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.2, duration: 0.5 }}
                            >
                                <Box sx={styles.customBannerBox}>
                                    <Typography variant='body1' sx={styles.customBannerBoxTitle}>
                                        {text}
                                    </Typography>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>
                </Grid>
                <Grid container sx={styles.linkContainer} direction='column'>
                    <Link sx={styles.linkMainCustom} href={translate('footer.privacylink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('privacy')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMainCustom} href={translate('footer.legalDisclaimerLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('legalDisclaimer')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMainCustom} href={translate('footer.accessibilityLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('accessibility')}
                    </Link>
                </Grid>
            </Grid>
        </Grid>
    </>

    const rbcdxLayoutHTML = <>
        <Grid container spacing={0} sx={styles.loginPage}>
            {leftRightContainerHTMLRBCDX}
            <Grid sx={styles.gridCustom} style={bannerBackground} item>
                <Box sx={styles.languageIconContainerMain}>
                    {languageIconHTML}
                </Box>
                <Grid container alignItems='start' sx={selectedLanguage === language.ENGLISH ? styles.containerDX : styles.containerDXFr}>
                    <motion.div
                        initial={{ opacity: 0, y: -100 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                    >
                        <Box sx={styles.bannerContentDX}>
                            <Typography variant='h1' sx={styles.welcomeSubHeaderDX}>
                                {translate(welcomeHeaderTitle)}
                            </Typography>
                            <Typography variant='body1' sx={styles.welcomeHeaderDescDX}>
                                {translate(welcomeHeaderDesc)}
                            </Typography>
                        </Box>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <Box sx={selectedLanguage === language.ENGLISH ? styles.rbcdxQRContainerEn : styles.rbcdxQRContainerFr}>
                            <Box sx={selectedLanguage === language.ENGLISH ? styles.rbcdxQRBoxContentEn : styles.rbcdxQRBoxContentFr}>
                                <Chip
                                    label={translate('rbcdx.new')}
                                    color='success'
                                    size='medium'
                                    variant='filled'
                                    sx={selectedLanguage === language.ENGLISH ? styles.rbcdxQRBoxChipEn : styles.rbcdxQRBoxChipFr}
                                />
                                <Typography sx={selectedLanguage === language.ENGLISH ? styles.rbcdxQRBoxTitleEn : styles.rbcdxQRBoxTitleFr}>
                                    {translate('rbcdx.qrBoxTitle')}
                                </Typography>
                            </Box>
                            <Box sx={selectedLanguage === language.ENGLISH ? styles.rbcdxQRCodeScannerEn : styles.rbcdxQRCodeScannerFr}>
                                <img
                                    src={qrCode}
                                    alt='QR Code Scanner'
                                    style={styles.rbcdxQRCodeScannerAsset}
                                />
                            </Box>
                        </Box>
                    </motion.div>
                </Grid>
                <Grid container sx={styles.linkContainer} direction='column'>
                    <Link sx={styles.linkMainCustom} href={translate('footer.privacylink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('privacy')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMainCustom} href={translate('footer.legalDisclaimerLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('legalDisclaimer')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMainCustom} href={translate('footer.accessibilityLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('accessibility')}
                    </Link>
                </Grid>
            </Grid>
        </Grid>
    </>

    const defaultLayoutHTML = <>
        <Grid container spacing={0} sx={styles.loginPage}>
            {leftRightContainerHTMLDefault}
            <Grid sx={styles.gridMain} style={bannerBackground} item>
                <Box sx={styles.languageIconContainerMain}>
                    {languageIconHTML} 
                </Box>
                <Grid container alignItems='start' sx={loginTitleLayoutType === loginTitleLayouts?.ALTERNATIVE ? styles.grid : styles.containerMain}>
                    <Grid sx={styles.header}>
                        {headerLogoSrc && (
                            <Box
                                alt={welcomeHeaderLogoAlt}
                                component='img'
                                src={headerLogoSrc}
                            />
                        )}
                    </Grid>
                    {loginTitleLayoutType === loginTitleLayouts?.ALTERNATIVE ? (
                        <Grid container alignItems='start' sx={styles.containerAlternative} direction='column'>
                            <Typography sx={styles.descriptionTop}>
                                {translate(welcomeHeaderDescTop)}
                            </Typography>
                            <Typography sx={styles.titleAlernative}>
                                {translate(welcomeHeaderTitle)}
                                <Box component='span' sx={styles.titleItalic}>{translate(welcomeHeaderTitleItalic)}</Box>
                            </Typography>
                            <Typography sx={styles.descriptionAlternative}>
                                {translate(welcomeHeaderDesc)}
                                <Link href={informationLink} target='_blank' sx={styles.link} underline='always' rel='noopener noreferrer'>
                                    {translate(informationLinkName)}
                                </Link>
                            </Typography>
                        </Grid>
                    ) : footerHTMLDefault}
                </Grid>
                <Grid container sx={styles.linkContainer} direction='column'>
                    <Link sx={styles.linkMain} href={translate('footer.privacylink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('privacy')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMain} href={translate('footer.legalDisclaimerLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('legalDisclaimer')}
                    </Link>
                    <Divider orientation='vertical' sx={styles.footerDivider} />
                    <Link sx={styles.linkMain} href={translate('footer.accessibilityLink')} underline='none' target='_blank' disabled={isProcessing} rel='noopener noreferrer'>
                        {translate('accessibility')}
                    </Link>
                </Grid>
            </Grid>
        </Grid>
    </>

    return (
        <Form id='loginForm' {...{
            enableReinitialize: !isProcessing && authStatus !== authStatusType.ERROR,
            initialValues: initValues,
            onSubmit: handleLoginClick,
            ref,
            validate
        }}>
            {(() => {
                switch (loginLayout) {
                    case loginLayoutType.CENTER:
                        return centralLayoutHTML
                    case loginLayoutType.LEFT_RIGHT:
                        return leftRightLayoutHTML
                    case loginLayoutType.MATA:
                        return mataLayoutHTML
                    case loginLayoutType.RBCDX:
                        return rbcdxLayoutHTML
                    default:
                        return defaultLayoutHTML
                }
            })()}
        </Form>
    )
}

DefaultLogin.propTypes = {
    applicationContext: PropTypes.object,
    classes: PropTypes.shape({
        container: PropTypes.string,
        description: PropTypes.string,
        grid: PropTypes.string,
        link: PropTypes.string,
        title: PropTypes.string,
    }).isRequired,
    errorAuth: PropTypes.object
}

export default DefaultLogin